import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "umi";
import { Justify } from "tea-component";
import { useLocal } from "@/pages/dashboard/utils/customHook";
import CalculationBubble from "@/pages/dashboard/pages/overview/components/CalculationBubble";
import { getSystemFlag } from "@/pages/dashboard/pages/overview/_config/system";
import { defaultPlatforms } from "@/pages/dashboard/pages/overview/_config/platforms";
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import { addLastDateRequestValue, formatFilterValues, isDateInCurrentMonth } from "@/pages/dashboard/utils/utils";
import { getDefaultUtcDateValue, getLastDate } from "@/pages/dashboard/utils/dateUtils";
import { getApiData } from '@/pages/dashboard/services/common';
import { FilterContext } from "@/pages/dashboard/components/PageContainer/context";
import moment from "moment";
import componentMap from "../../_config/compConfig";
import Render from "../PageContainer/Render";
import { IComponentType } from "../PageContainer/type";

export default function KeyMetricTitle(props: any) {
  const {
    className,
    style,
    config,
    data,
    logConfig,
    ...restProps
  } = props;
  const {
    metric,
    api,
    titleCn,
    titleEn,
    dateFilterType = 'rangeDaily',
    haveLastDate,
    switchSlot,
    rightSlot,
  } = config || {};
  const switchData = props?.data;
  const timeZone = parseInt(props?.data?.time_zone || '', 10) || 0;
  const { isEN, getLocalText, locale } = useLocal();
  const title = isEN ? titleEn : titleCn;
  const contextObj = useContext(FilterContext);
  const { filterVal, filterLoading } = contextObj || {};
  const { rangeTime, timeType } = filterVal || {};

  const { selector, gameCode } = useSelector((state: any) => ({
    selector: state?.dashboardSetting?.selector,
    gameCode: state?.dashboard?.currentGame?.game_code,
  }), shallowEqual);

  const [loading, setLoading] = useState(true);
  const [platFormData, setPlatFormData] = useState([]);

  const platformList = useMemo(() => {
    const os_list = selector?.os_list?.filter((item: any) => item?.os_id !== '255');
    return os_list;
  }, [locale, selector]);

  useEffect(() => {
    if (filterLoading || !api) return;
    getPlatFormData();
  }, [filterVal]);

  const dateStr: string = useMemo(() => {
    const singleDailyObj: { [key: string]: string; } = {
      singleDaily: 'singleDaily',
      contrastDaily: 'contrastDaily'
    };
    const dateValKey = singleDailyObj?.[dateFilterType];
    if (dateValKey) {
      return filterVal?.[dateValKey];
    }
    const start = rangeTime?.[0];
    let end = rangeTime?.[1];
    if (end && isDateInCurrentMonth(end, timeZone) && haveLastDate) {
      return `${start} ~ ${getLastDate(timeZone)}`;
    }
    if (end && timeType === 'Month') {
      // 筛选器传出的endDate为1号，这里转一下为对应月的最后一天
      end = (moment(end, 'YYYY-MM-DD'))?.endOf('month')?.format('YYYY-MM-DD');
    }

    return rangeTime?.length > 1 ? `${start} ${end && (end !== start) ? ` ~ ${end}` : ""}` : '';
  }, [dateFilterType, filterVal, timeZone, haveLastDate]);
  const getPlatFormData = () => {
    const { timeType, diff, rangeTime, singleDaily, ...filters } = filterVal || {};
    const defaultTimeRange = getDefaultUtcDateValue(timeType, rangeTime);
    const rangeTimeArr = diff ? defaultTimeRange : rangeTime;
    const [start, end] = rangeTimeArr;
    let params = {
      game_code: gameCode,
      start_time: start,
      end_time: end,
      period: PERIODTIMEOBJ?.[timeType],
      filters: { ...formatFilterValues(filters), os_list: ["255"] },
      by_type: 'os',
      has_top_data: true,
      metric,
      top_num: 1000,
    };
    setLoading(true);
    params = addLastDateRequestValue(params, haveLastDate, timeZone);
    getApiData(api, params).then((res) => {
      setPlatFormData(res?.dwm_list || []);
      setLoading(false);
    }).catch(() => {
      setPlatFormData([]);
      setLoading(false);
    });
  };

  const bubbleList = useMemo(() => {
    if (!api) return [];
    return platformList?.map((item: any) => {
      const dataItem = platFormData?.find((v: any) => v?.name === item?.os_id);
      const name = item?.os_name_en?.trim()?.toLowerCase();
      const icon = getSystemFlag(name) || defaultPlatforms?.icon;
      if (dataItem && dataItem?.value !== '') {
        return {
          title: item?.os_name_en,
          icon,
          hasData: true
        };
      }
      return {
        title: item?.os_name_en,
        icon,
        hasData: false
      };
    }) || [];
  }, [platformList, platFormData]);

  const renderSwitchSlot = useCallback(() => {
    if (!switchSlot) return null;
    const { name, props: switchSlotProps, ...resetProps } = switchSlot || {};
    const SwitchSlotCom = componentMap?.[name];
    return SwitchSlotCom ? <SwitchSlotCom {...(resetProps || {})} {...(switchSlotProps || {})} switchData={switchData} changeData={props?.changeData}  /> : null;
  }, [switchSlot,switchData]);

  const renderRightSlot = useCallback(() => {
    if (!rightSlot) return null;
    const { name, props: slotProps, ...resetProps } = rightSlot || {};
    const RightSlotCom = componentMap?.[name];
    return RightSlotCom ? (
      <RightSlotCom
        {...(resetProps || {})}
        {...(slotProps || {})}
        {...restProps}
        data={data}
        logConfig={logConfig}
      />
    ) : null;
  }, [rightSlot, data, logConfig, restProps]);

  const switchSlotChildren = () => {
    const childrenList = switchSlot?.props?.componentMap?.[switchData?.cardTitleSwitchVal];
    return <>
      {
        childrenList?.map((item: IComponentType | undefined, index: any) => {
          return <Render
            {...restProps}
            config={{ ...(item || {}), switchData, }}
            logConfig={logConfig}
            key={item?.name + index}
          />;
        })
      }
    </>;
  };

  return <div className={`dbd-overlay-card ${className}`} style={style} >
    <Justify
      className="overlay-margin"
      left={
        <div className="overlay-title">
          {title ? title : getLocalText('overview_Key_Performance_Metrics')}
          {api && <CalculationBubble
            tipList={bubbleList}
            isLoading={loading}
          />}
          {switchSlot && renderSwitchSlot?.()}
        </div>
      }
      right={
        <div className="overlay-time dbd-key-metric-title-time">
          {renderRightSlot()}
          <span className="overlay-time-date">{dateStr || ''}</span>
        </div>
      }
    />
    {switchSlotChildren?.()}
  </div>;
}
